import type { LangChainMessage } from "@assistant-ui/react-langgraph";

import { AGENT_QUESTION_RESPONSES_METADATA_KEY } from "@/agents-v2/message-metadata/metadata-only-message";

import {
  asArray,
  asRecord,
  getStringValue,
  parseMaybeJson
} from "@/agents-v2/utils/unknown";

export const ASK_QUESTION_TOOL_NAME = "ask_question";
/** Interrupt `type` the backend's `ask_question` tool pauses the run with. */
export const ASK_QUESTION_INTERRUPT_TYPE = "ask_question";
export { AGENT_QUESTION_RESPONSES_METADATA_KEY } from "@/agents-v2/message-metadata/metadata-only-message";

export type QuestionAnswerType = "text" | "single_select" | "multi_select";

export type QuestionOption = {
  label: string;
  tooltip: string | null;
  requiresClarification: boolean;
  clarificationPlaceholder: string | null;
  /** The agent's pick, with its reason shown beside the label. */
  recommended: boolean;
  recommendationReason: string | null;
};

export type ThreadQuestion = {
  question: string;
  answerType: QuestionAnswerType;
  placeholder: string | null;
  options: QuestionOption[];
};

export type ThreadQuestionSet = {
  title: string;
  description: string | null;
  questions: ThreadQuestion[];
};

export type QuestionDraftAnswer = {
  text: string;
  selectedOptions: string[];
  clarifications: Record<string, string>;
};

export type QuestionResponseAnswer = {
  question_index: number;
  question: string;
  answer_type: QuestionAnswerType;
  text?: string;
  selected_options?: Array<{
    label: string;
    clarification?: string;
  }>;
};

export type QuestionResponseSet = {
  tool_call_id: string;
  answers: QuestionResponseAnswer[];
};

export type QuestionLifecycle = {
  locked: boolean;
  persistedResponse: QuestionResponseSet | null;
};

/** A run paused on an `ask_question` call, waiting for that card's answers. */
export type AskQuestionInterrupt = {
  toolCallId: string;
};

/** What the card sends back to resume the paused run. */
export type QuestionResumePayload = {
  type: typeof ASK_QUESTION_INTERRUPT_TYPE;
  tool_call_id: string;
  answers: QuestionResponseAnswer[];
};

const findLastIndex = <T>(
  values: readonly T[],
  predicate: (value: T) => boolean
): number => {
  for (let index = values.length - 1; index >= 0; index -= 1) {
    if (predicate(values[index])) return index;
  }
  return -1;
};

export const emptyQuestionAnswer = (): QuestionDraftAnswer => ({
  text: "",
  selectedOptions: [],
  clarifications: {}
});

const parseAnswerType = (value: unknown): QuestionAnswerType | null =>
  value === "text" || value === "single_select" || value === "multi_select"
    ? value
    : null;

const parseOption = (value: unknown): QuestionOption | null => {
  const record = asRecord(value);
  const label = getStringValue(record, "label");
  if (!label) return null;

  const recommended = record.recommended === true;
  return {
    label,
    tooltip: getStringValue(record, "tooltip"),
    requiresClarification: record.requires_clarification === true,
    clarificationPlaceholder: getStringValue(
      record,
      "clarification_placeholder"
    ),
    recommended,
    recommendationReason: recommended
      ? getStringValue(record, "recommendation_reason")
      : null
  };
};

const parseQuestion = (value: unknown): ThreadQuestion | null => {
  const record = asRecord(value);
  const question = getStringValue(record, "question");
  const answerType =
    parseAnswerType(record.answer_type) ??
    (record.multi_select === true ? "multi_select" : "single_select");
  if (!question || !answerType) return null;

  const options = asArray(record.options)
    .map(parseOption)
    .filter((option): option is QuestionOption => option !== null);

  if (answerType === "text" && options.length > 0) return null;
  if (answerType !== "text" && (options.length < 2 || options.length > 4)) {
    return null;
  }

  return {
    question,
    answerType,
    placeholder: getStringValue(record, "placeholder"),
    options
  };
};

export const parseQuestionSet = (value: unknown): ThreadQuestionSet | null => {
  const record = asRecord(parseMaybeJson(value));
  const parsedQuestions = asArray(record.questions).map(parseQuestion);
  if (parsedQuestions.length === 0) return null;
  if (parsedQuestions.some((question) => question === null)) return null;

  const questions = parsedQuestions.filter(
    (question): question is ThreadQuestion => question !== null
  );

  return {
    title: getStringValue(record, "title") ?? "Quick questions",
    description: getStringValue(record, "description"),
    questions
  };
};

export const isQuestionAnswered = (
  question: ThreadQuestion,
  answer: QuestionDraftAnswer | undefined
): boolean => {
  if (!answer) return false;
  if (question.answerType === "text") return Boolean(answer.text.trim());

  const selected = question.options.filter((option) =>
    answer.selectedOptions.includes(option.label)
  );
  if (selected.length === 0) return false;
  if (question.answerType === "single_select" && selected.length !== 1) {
    return false;
  }

  return selected.every(
    (option) =>
      !option.requiresClarification ||
      Boolean(answer.clarifications[option.label]?.trim())
  );
};

export const buildQuestionResponseSet = (
  toolCallId: string,
  questionSet: ThreadQuestionSet,
  answers: QuestionDraftAnswer[]
): QuestionResponseSet | null => {
  const responseAnswers = questionSet.questions.flatMap(
    (question, questionIndex): QuestionResponseAnswer[] => {
      const answer = answers[questionIndex];
      if (!isQuestionAnswered(question, answer) || !answer) return [];

      if (question.answerType === "text") {
        return [
          {
            question_index: questionIndex,
            question: question.question,
            answer_type: question.answerType,
            text: answer.text.trim()
          }
        ];
      }

      return [
        {
          question_index: questionIndex,
          question: question.question,
          answer_type: question.answerType,
          selected_options: question.options
            .filter((option) => answer.selectedOptions.includes(option.label))
            .map((option) => ({
              label: option.label,
              ...(answer.clarifications[option.label]?.trim() && {
                clarification: answer.clarifications[option.label].trim()
              })
            }))
        }
      ];
    }
  );

  return responseAnswers.length > 0
    ? { tool_call_id: toolCallId, answers: responseAnswers }
    : null;
};

const parseResponseAnswer = (value: unknown): QuestionResponseAnswer | null => {
  const record = asRecord(value);
  const questionIndex = record.question_index;
  const question = getStringValue(record, "question");
  const answerType = parseAnswerType(record.answer_type);
  if (
    typeof questionIndex !== "number" ||
    questionIndex < 0 ||
    !question ||
    !answerType
  ) {
    return null;
  }

  if (answerType === "text") {
    const text = getStringValue(record, "text");
    return text
      ? {
          question_index: questionIndex,
          question,
          answer_type: answerType,
          text
        }
      : null;
  }

  // flatMap instead of the web app's map+filter type predicate: the pane
  // compiles without strictNullChecks, where that predicate does not type.
  const selectedOptions = asArray(record.selected_options).flatMap(
    (option): { label: string; clarification?: string }[] => {
      const optionRecord = asRecord(option);
      const label = getStringValue(optionRecord, "label");
      if (!label) return [];
      const clarification = getStringValue(optionRecord, "clarification");
      return [{ label, ...(clarification && { clarification }) }];
    }
  );
  if (selectedOptions.length === 0) return null;

  return {
    question_index: questionIndex,
    question,
    answer_type: answerType,
    selected_options: selectedOptions
  };
};

export const parseQuestionResponseSets = (
  value: unknown
): QuestionResponseSet[] =>
  asArray(value).flatMap((responseSet) => {
    const record = asRecord(responseSet);
    const toolCallId = getStringValue(record, "tool_call_id");
    const answers = asArray(record.answers)
      .map(parseResponseAnswer)
      .filter((answer): answer is QuestionResponseAnswer => answer !== null);
    return toolCallId && answers.length > 0
      ? [{ tool_call_id: toolCallId, answers }]
      : [];
  });

/**
 * Read the `ask_question` interrupt a paused run is waiting on.
 * @param value - The raw interrupt value from the runtime.
 * @returns The paused call, or null for any other (or no) interrupt.
 */
export const parseAskQuestionInterrupt = (
  value: unknown
): AskQuestionInterrupt | null => {
  const record = asRecord(parseMaybeJson(value));
  if (record.type !== ASK_QUESTION_INTERRUPT_TYPE) return null;
  const toolCallId = getStringValue(record, "tool_call_id");
  return toolCallId ? { toolCallId } : null;
};

/**
 * Build the payload that resumes a paused `ask_question` call.
 * @param toolCallId - The call the run is paused on.
 * @param response - The answers, or null when the user skipped the round.
 * @returns The resume payload the tool turns into its result.
 */
export const toQuestionResumePayload = (
  toolCallId: string,
  response: QuestionResponseSet | null
): QuestionResumePayload => ({
  type: ASK_QUESTION_INTERRUPT_TYPE,
  tool_call_id: toolCallId,
  answers: response?.answers ?? []
});

/** The outcome an answered (or skipped) `ask_question` call reports back. */
export type QuestionToolOutcome = {
  status: "answered" | "skipped" | "not_asked";
  answers: QuestionResponseAnswer[];
};

/**
 * Read the outcome from an `ask_question` tool result.
 *
 * Only the post-interrupt shape reports a `status`; the legacy result was the
 * question payload itself, which parses to null here so those cards still fall
 * back to the metadata written onto the following human message.
 * @param value - The tool result.
 * @returns The outcome, or null when the result is not one.
 */
export const parseQuestionToolOutcome = (
  value: unknown
): QuestionToolOutcome | null => {
  const record = asRecord(parseMaybeJson(value));
  const status = getStringValue(record, "status");
  if (status !== "answered" && status !== "skipped" && status !== "not_asked") {
    return null;
  }
  const answers = asArray(record.answers)
    .map(parseResponseAnswer)
    .filter((answer): answer is QuestionResponseAnswer => answer !== null);
  return { status, answers };
};

/**
 * Whether a card predates the interrupt and is answered by the next message.
 *
 * The old tool returned the question payload as its result and read the
 * answers off the following human message's metadata. A card paused on an
 * interrupt has no result at all, so the two are told apart by what the call
 * carries rather than by any flag on the thread.
 * @param messages - The thread's messages.
 * @param toolCallId - The card's tool call id.
 * @returns True when answers for this card must travel on a human message.
 */
export const isLegacyQuestionCard = (
  messages: readonly unknown[],
  toolCallId: string
): boolean => {
  const result = questionToolPart(messages, toolCallId)?.result;
  if (result === undefined || parseQuestionToolOutcome(result)) return false;
  return parseQuestionSet(result) !== null;
};

const questionToolPart = (
  messages: readonly unknown[],
  toolCallId: string
): Record<string, unknown> | null => {
  for (const message of messages) {
    for (const part of asArray(asRecord(message).parts)) {
      const record = asRecord(part);
      if (
        record.type === "tool-call" &&
        record.toolName === ASK_QUESTION_TOOL_NAME &&
        record.toolCallId === toolCallId
      ) {
        return record;
      }
    }
  }
  return null;
};

export const draftAnswersFromResponse = (
  questionSet: ThreadQuestionSet,
  response: QuestionResponseSet
): QuestionDraftAnswer[] =>
  questionSet.questions.map((_, questionIndex) => {
    const persisted = response.answers.find(
      (answer) => answer.question_index === questionIndex
    );
    if (!persisted) return emptyQuestionAnswer();

    return {
      text: persisted.text ?? "",
      selectedOptions:
        persisted.selected_options?.map((option) => option.label) ?? [],
      clarifications: Object.fromEntries(
        (persisted.selected_options ?? []).flatMap((option) =>
          option.clarification
            ? [[option.label, option.clarification] as const]
            : []
        )
      )
    };
  });

const responseSetsFromUserMessage = (
  message: unknown
): QuestionResponseSet[] => {
  const custom = asRecord(asRecord(message).metadata).custom;
  return parseQuestionResponseSets(
    asRecord(custom)[AGENT_QUESTION_RESPONSES_METADATA_KEY]
  );
};

export const resolveQuestionLifecycle = (
  messages: readonly unknown[],
  toolCallId: string
): QuestionLifecycle => {
  // The answers now come back as the tool's own result, so a card is settled
  // the moment its call reports one - there is no following human message to
  // wait for. Cards from before the interrupt still fall through below.
  const outcome = parseQuestionToolOutcome(
    questionToolPart(messages, toolCallId)?.result
  );
  if (outcome) {
    return {
      locked: true,
      persistedResponse:
        outcome.answers.length > 0
          ? { tool_call_id: toolCallId, answers: outcome.answers }
          : null
    };
  }

  const ownerIndex = messages.findIndex((message) =>
    asArray(asRecord(message).parts).some(
      (part) =>
        asRecord(part).type === "tool-call" &&
        asRecord(part).toolCallId === toolCallId
    )
  );
  if (ownerIndex < 0) return { locked: false, persistedResponse: null };

  const nextUserMessage = messages
    .slice(ownerIndex + 1)
    .find((message) => asRecord(message).role === "user");
  if (!nextUserMessage) return { locked: false, persistedResponse: null };

  return {
    locked: true,
    persistedResponse:
      responseSetsFromUserMessage(nextUserMessage).find(
        (response) => response.tool_call_id === toolCallId
      ) ?? null
  };
};

export const getActiveQuestionToolCallIds = (
  messages: readonly unknown[]
): string[] => {
  const lastUserIndex = findLastIndex(
    messages,
    (message) => asRecord(message).role === "user"
  );

  return messages.slice(lastUserIndex + 1).flatMap((message) =>
    asArray(asRecord(message).parts).flatMap((part) => {
      const record = asRecord(part);
      // A card whose call already reported an outcome is settled: its answers
      // travelled as the tool result, so it must not also ride on the next
      // human message as pending draft metadata.
      if (parseQuestionToolOutcome(record.result)) return [];
      return record.type === "tool-call" &&
        record.toolName === ASK_QUESTION_TOOL_NAME &&
        record.isError !== true &&
        asRecord(record.status).type !== "incomplete" &&
        typeof record.toolCallId === "string"
        ? [record.toolCallId]
        : [];
    })
  );
};

export const attachQuestionResponses = (
  messages: LangChainMessage[],
  responseSets: QuestionResponseSet[]
): LangChainMessage[] => {
  if (responseSets.length === 0) return messages;
  const humanIndex = findLastIndex(
    messages,
    (message) => message.type === "human"
  );
  if (humanIndex < 0) return messages;

  const humanMessage = messages[humanIndex];
  if (humanMessage.type !== "human") return messages;
  const additionalKwargs = humanMessage.additional_kwargs ?? {};
  const metadata = asRecord(additionalKwargs.metadata);
  const existingSets = parseQuestionResponseSets(
    metadata[AGENT_QUESTION_RESPONSES_METADATA_KEY]
  );
  const pendingIds = new Set(
    responseSets.map((response) => response.tool_call_id)
  );

  const nextMessages = [...messages];
  nextMessages[humanIndex] = {
    ...humanMessage,
    additional_kwargs: {
      ...additionalKwargs,
      metadata: {
        ...metadata,
        [AGENT_QUESTION_RESPONSES_METADATA_KEY]: [
          ...existingSets.filter(
            (response) => !pendingIds.has(response.tool_call_id)
          ),
          ...responseSets
        ]
      }
    }
  };
  return nextMessages;
};
